import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { CircularProgress } from '@startuptools/ui/CircularProgress';
import { IconButton } from '@startuptools/ui/IconButton';
import { Input } from '@startuptools/ui/Input';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import TablePagination from '@mui/material/TablePagination';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Suspense, useState } from 'react';
import { Trans, useLingui } from '@lingui/react';
import { Identity } from '@startuptools/business-ui/identity/Identity';
import { toNumber } from 'lodash-es';
import { useForm } from 'react-hook-form';
import { useDebounce } from '@startuptools/common/react-hooks';
import {
  MeetingParticipantsDocument,
  useCreateMeetingParticipantMutation,
  useDeleteMeetingParticipantMutation,
} from '../../../graphql/react-operations';
import { identityToInput } from '@startuptools/business-ui/identity/identity-to-input';
import { JuridicalKind } from '../../../models/identity.model';
import { InputState } from '@startuptools/business-ui/identity/types';
import { AddIdentity } from '@startuptools/business-ui/identity/add/AddIdentity';
import { IdentityEditableEmail } from '../../../components/editable-email/IdentityEditableEmail';
import { GqlMeeting, GqlMeetingKind, GqlMpKind } from '../../../graphql/base-types.graphql';
import {
  MeetingParticipantsTableInnerDocument,
  useMeetingParticipantsTableInnerSuspenseQuery,
} from './MeetingParticipantsTable.graphql';
import { ParticipantsSummonsDocument } from './edit-steps/ParticipantsSummons.graphql';
import { BoardMeetingDocument } from '../../board-meetings/BoardMeeting.graphql';
import { ShareholdersMeetingDocument } from '../../shareholders-meetings/ShareholdersMeeting.graphql';

export const MeetingParticipantsTable = ({ meeting }: { meeting: Pick<GqlMeeting, 'id' | 'kind'> }) => {
  const companyId = useCompanyId();
  const { i18n } = useLingui();
  const [openAddMeetingParticipant, setOpenAddMeetingParticipant] = useState(false);
  // eslint-disable-next-line sut/loading-mutation
  const [doCreate] = useCreateMeetingParticipantMutation({
    refetchQueries: [
      BoardMeetingDocument,
      ShareholdersMeetingDocument,
      MeetingParticipantsTableInnerDocument,
      ParticipantsSummonsDocument,
      MeetingParticipantsDocument,
    ],
  });
  const { register, watch } = useForm<{ searchString: string }>({ defaultValues: { searchString: '' } });
  const searchString = watch('searchString');
  const debouncedSearchString = useDebounce(searchString);

  return (
    <>
      <Stack gap={2}>
        {meeting.kind !== GqlMeetingKind.BoardMeeting && (
          <Alert startDecorator={<InfoOutlinedIcon />} variant="outlined" color="neutral">
            <Trans id="You may add meeting participants which aren't shareholders here, for example the board and/or the accountant" />
          </Alert>
        )}

        <Box>
          <Button variant="outlined" color="primary" onClick={() => setOpenAddMeetingParticipant(true)}>
            <Trans id="Add meeting participant" />
          </Button>
        </Box>

        <Input
          {...register('searchString')}
          startDecorator={searchString === debouncedSearchString ? <SearchIcon /> : <CircularProgress size="sm" />}
          type="search"
          placeholder={i18n._('Search...')}
        />

        <Suspense fallback={'Loading'}>
          <MeetingParticipantsTableInner
            companyId={companyId}
            meetingId={meeting.id}
            searchString={debouncedSearchString}
          />
        </Suspense>
      </Stack>
      {openAddMeetingParticipant && (
        <AddIdentity
          headline={<Trans id="Add meeting participant" />}
          availableInputStates={{
            [InputState.System]: true,
            [InputState.Swedish]: true,
            [InputState.Foreign]: true,
            [InputState.CompanyBoard]: false,
          }}
          juridicalKinds={[JuridicalKind.Person]}
          relations={{
            companyDirector: true,
            meetingParticipant: true,
          }}
          onClose={() => setOpenAddMeetingParticipant(false)}
          handleCreate={async identity => {
            return doCreate({
              variables: {
                companyId,
                input: {
                  adjuster: false,
                  chairman: false,
                  secretary: false,
                  present: true,
                  meetingId: meeting.id,
                  isShareholder: false,
                  kind: GqlMpKind.Guest,
                  identityInput: identityToInput(identity),
                },
              },
            }).then(() => setOpenAddMeetingParticipant(false));
          }}
        />
      )}
    </>
  );
};

const MeetingParticipantsTableInner = ({
  companyId,
  meetingId,
  searchString,
}: {
  companyId: string;
  meetingId: string;
  searchString: string;
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data } = useMeetingParticipantsTableInnerSuspenseQuery({
    variables: {
      companyId,
      meetingId,
      filter: { identity: searchString.length > 0 ? { name: `%${searchString}%` } : null, isShareholder: false },
      offset: rowsPerPage * page,
      limit: rowsPerPage,
    },
  });

  // eslint-disable-next-line sut/loading-mutation
  const [doDelete] = useDeleteMeetingParticipantMutation({
    refetchQueries: [MeetingParticipantsTableInnerDocument, ParticipantsSummonsDocument],
  });

  const handleRowsPerPageChange = (rowsPerPage: number) => {
    setRowsPerPage(rowsPerPage);
    setPage(0);
  };

  return (
    <>
      <Table>
        <thead>
          <tr>
            <th>
              <Trans id="Participant" />
            </th>
            <th>
              <Trans id="Email" />
            </th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {data.meetingParticipants.models.map(mp => (
            <tr key={mp.id}>
              <td>
                <Identity identity={mp.identity} editable={false} />
              </td>
              <td>
                <IdentityEditableEmail id={mp.identity.id} refetchQueries={[MeetingParticipantsTableInnerDocument]}>
                  {mp.identity.email}
                </IdentityEditableEmail>
              </td>
              <td align="right">
                <IconButton color="danger" onClick={() => doDelete({ variables: { companyId, id: mp.id } })}>
                  <DeleteIcon />
                </IconButton>
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <TablePagination
              colSpan={3}
              count={data.meetingParticipants.totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(_, newPage) => setPage(newPage)}
              onRowsPerPageChange={event => handleRowsPerPageChange(toNumber(event.target.value))}
              labelRowsPerPage={<Trans id="Rows per page:" />}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </tr>
        </tfoot>
      </Table>
    </>
  );
};
